/* =============================================================
   SEARCH — DATA ACCESS
   =============================================================
   "Find a berth" in two calls: the places a search can start
   from (the berth_cities view), and search_berths(), which judges
   every berth near a point for one vessel and one stay -- fit,
   missing amenities, price and availability -- in a single round
   trip. See supabase/migrations/20261004120512_create_search_berths.

   Nothing is filtered here beyond distance. Which results to hide
   and which to grey out is the screen's decision.
   ============================================================= */

import { supabase } from './supabase'
import { toDataError } from './errors'
import type { AmenityCode, BerthCity, BerthResult } from './types'

/** Every city with berths, most berths first, then by name. */
export async function listCities(): Promise<BerthCity[]> {
  const { data, error } = await supabase
    .from('berth_cities')
    .select('city, country_code, latitude, longitude, berths')
    .order('berths', { ascending: false })
    .order('city', { ascending: true })

  if (error) throw toDataError(error, 'Could not load places to search.')
  return (data ?? []) as BerthCity[]
}

export interface SearchParams {
  lat: number
  lng: number
  radiusKm?: number
  vessel?: { length_m: number; width_m: number; draft_m: number } | null
  amenities?: AmenityCode[]
  /** ISO instants. Both or neither -- without them there is no price and no availability. */
  arrival?: string | null
  departure?: string | null
}

export async function searchBerths(params: SearchParams): Promise<BerthResult[]> {
  const { data, error } = await supabase.rpc('search_berths', {
    p_lat: params.lat,
    p_lng: params.lng,
    p_radius_km: params.radiusKm ?? 25,
    p_vessel_length_m: params.vessel?.length_m ?? null,
    p_vessel_width_m: params.vessel?.width_m ?? null,
    p_vessel_draft_m: params.vessel?.draft_m ?? null,
    p_amenities: params.amenities ?? [],
    p_arrival: params.arrival ?? null,
    p_departure: params.departure ?? null,
  })

  if (error) throw toDataError(error, 'Could not search berths.')
  return (data ?? []) as BerthResult[]
}

/**
 * Search, centred on one berth: that berth's own result, and its neighbours
 * as alternatives. What the berth profile and the booking page both need --
 * the same fit, price and availability the search list showed, so a price
 * never changes between the list and the booking.
 */
export async function searchAroundBerth(
  berth: { id: number; latitude: number; longitude: number },
  params: Omit<SearchParams, 'lat' | 'lng'>,
): Promise<{ result: BerthResult | null; nearby: BerthResult[] }> {
  const rows = await searchBerths({ ...params, lat: berth.latitude, lng: berth.longitude })
  return {
    result: rows.find((r) => r.id === berth.id) ?? null,
    nearby: rows.filter((r) => r.id !== berth.id),
  }
}
